import { useId, useState } from 'react';
import { messages } from '../i18n/ja';

/**
 * 数値の入力欄。入力中は反映せず、Enter またはフォーカスが外れたときに確定する（1 回の編集を 1 手にするため）。
 * 不正な値（数値でない・範囲外）は、理由を示して反映しない。
 *
 * 値が外から変わったときは、親が `key` を変えて作り直す（入力途中の文字を捨てて、新しい値を表示する）。
 */
export function NumberField({
  label,
  unit,
  value,
  decimals,
  step,
  onCommit,
  testId,
}: {
  readonly label: string;
  readonly unit: string;
  readonly value: number;
  /** 表示する小数の桁数 */
  readonly decimals: number;
  readonly step: number;
  /** 確定する。反映できなければ、理由（日本語）を返す */
  readonly onCommit: (value: number) => string | undefined;
  readonly testId: string;
}) {
  const id = useId();
  const shown = value.toFixed(decimals);
  const [draft, setDraft] = useState(shown);
  const [error, setError] = useState<string | undefined>();

  const commit = (): void => {
    if (draft.trim() === shown) {
      setError(undefined);
      return;
    }
    const parsed = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(parsed)) {
      setError(messages.properties.notANumber);
      return;
    }
    setError(onCommit(parsed));
  };

  return (
    <div className="number-field">
      <label htmlFor={id} className="number-field__label">
        {label}
      </label>
      <div className="number-field__input">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          value={draft}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : `${id}-error`}
          data-testid={testId}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            } else if (event.key === 'Escape') {
              setDraft(shown);
              setError(undefined);
            }
          }}
        />
        <span className="number-field__unit">{unit}</span>
      </div>
      {error !== undefined && (
        <p
          id={`${id}-error`}
          className="number-field__error"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
}
